// User-facing description of the Supabase password policy. Mirrored from the
// Auth settings in Supabase Studio (Authentication → Policies). Update here
// when those policies change.
export const PASSWORD_REQUIREMENTS_HINT =
  "At least 8 characters, with a lowercase letter, uppercase letter, digit, and symbol.";

// Parse and translate the Supabase Auth failure params that land on /auth,
// either in the URL hash (Supabase's default for link verification failures)
// or in the query string (/auth/callback redirects to `/auth?error_code=...`).
// Pure, no DOM and no React, so both server and client can import it.

export interface AuthErrorParams {
  error?: string;
  error_code?: string;
  error_description?: string;
}

const AUTH_KEYS = ["error", "error_code", "error_description"] as const;

export function parseAuthHash(hash: string): AuthErrorParams {
  if (!hash) return {};
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!stripped) return {};
  const params = new URLSearchParams(stripped);
  const out: AuthErrorParams = {};
  for (const key of AUTH_KEYS) {
    const value = params.get(key);
    if (value) out[key] = value;
  }
  return out;
}

const EXPIRED_LINK_MESSAGE =
  "That link expired. Enter your email and we'll send a new one.";

// PKCE keeps the code verifier in a cookie of the browser that asked for the
// link. Opened anywhere else, auth-js returns pkce_code_verifier_not_found.
const OTHER_BROWSER_MESSAGE =
  "Open the link in the browser where you asked for it, or request a new one.";

// Each request in a browser (a link for another address, Google, a password
// reset) replaces its one code verifier. An older link that Supabase still
// accepts then returns bad_code_verifier.
const EARLIER_REQUEST_MESSAGE =
  "That link is from an earlier request. Open the newest email we sent, or request a new link.";

const ACCESS_DENIED_MESSAGE =
  "Access was denied. Your link may have expired, so try requesting a new one.";

// Codes /auth/update-password sends, so /auth never has to render text it
// took from the URL.
export const RESET_LINK_EXPIRED = "reset_link_expired";
export const RESET_CHECK_FAILED = "reset_check_failed";
const OWN_CODE_MESSAGES: Record<string, string> = {
  [RESET_LINK_EXPIRED]: "That password reset link expired. Request a new one.",
  [RESET_CHECK_FAILED]:
    "We couldn't open that password reset link. Request a new one.",
};

const UNKNOWN_ERROR_MESSAGE =
  "We couldn't log you in with that link. Try again or request a new one.";

const EXPIRED_LINK_CODES = [
  "otp_expired",
  "flow_state_expired",
  "flow_state_not_found",
];
const OTHER_BROWSER_CODE = "pkce_code_verifier_not_found";
const EARLIER_REQUEST_CODE = "bad_code_verifier";
const LINK_ERROR_CODES = [
  ...EXPIRED_LINK_CODES,
  OTHER_BROWSER_CODE,
  EARLIER_REQUEST_CODE,
];

// /auth/update-password turns a failed code exchange into a code for /auth.
// Verifier errors keep their own message; anything else reads as expired.
export function resetLinkErrorCode(error: { code?: string } | null): string {
  const code = error?.code ?? "";
  return code === OTHER_BROWSER_CODE || code === EARLIER_REQUEST_CODE
    ? code
    : RESET_LINK_EXPIRED;
}

export function interpretAuthError(params: AuthErrorParams): string | null {
  const code = params.error_code ?? "";
  if (EXPIRED_LINK_CODES.includes(code)) return EXPIRED_LINK_MESSAGE;
  if (code === OTHER_BROWSER_CODE) return OTHER_BROWSER_MESSAGE;
  if (code === EARLIER_REQUEST_CODE) return EARLIER_REQUEST_MESSAGE;
  if (Object.hasOwn(OWN_CODE_MESSAGES, code)) return OWN_CODE_MESSAGES[code];
  if (params.error === "access_denied") return ACCESS_DENIED_MESSAGE;
  // Anyone can craft these params, so their text is never shown. The /auth
  // page logs them.
  if (params.error || params.error_code || params.error_description) {
    return UNKNOWN_ERROR_MESSAGE;
  }
  return null;
}

// `?message=` on /auth carries a code, never text, so a crafted link cannot
// put its own words on the page.
export const PASSWORD_UPDATED = "password_updated";
const AUTH_MESSAGES: Record<string, string> = {
  [PASSWORD_UPDATED]: "Password updated. Log in with your new password.",
};

export function interpretAuthMessage(code: string | undefined): string | null {
  return code && Object.hasOwn(AUTH_MESSAGES, code)
    ? AUTH_MESSAGES[code]
    : null;
}

// Link errors open the email form, so a new link is one click away. Null means
// the error belongs to no form and the page opens as it would without it.
export type AuthErrorFlow = "email" | null;
export function flowForAuthError(params: AuthErrorParams): AuthErrorFlow {
  return LINK_ERROR_CODES.includes(params.error_code ?? "") ? "email" : null;
}

// Pulls auth-error params out of a URL or relative path, checking both the
// query string and the hash fragment. An auth error can arrive inside
// `returnUrl=...` after middleware sent a signed-out visitor to /auth, for
// example an OAuth flow that landed on `/?error=...` and became
// `/auth?returnUrl=/?error=...`.
export function extractAuthErrorFromUrl(url: string): AuthErrorParams {
  if (!url) return {};
  let parsed: URL;
  try {
    // Use a placeholder base so relative paths like "/?error=..." parse.
    parsed = new URL(url, "http://placeholder.local");
  } catch {
    return {};
  }
  const fromQuery: AuthErrorParams = {};
  for (const key of AUTH_KEYS) {
    const value = parsed.searchParams.get(key);
    if (value) fromQuery[key] = value;
  }
  const fromHash = parseAuthHash(parsed.hash);
  // Hash takes precedence — Supabase puts link-verification failures there
  // specifically, and we want that signal over any incidental query params.
  return Object.keys(fromHash).length > 0 ? fromHash : fromQuery;
}
